'use client';

import { useCallback, useRef, useEffect, useState } from 'react';
import { TrackPixelPayload, PixelTrackingResult } from '@/types/facebook-pixel';
import { trackPixelEvent } from '@/app/actions/track-pixel';

interface UseFacebookPixelOptions {
  enabled?: boolean;
  debounceMs?: number;
  onSuccess?: (result: PixelTrackingResult) => void;
  onError?: (error: Error) => void;
}

interface UseFacebookPixelReturn {
  trackEvent: (payload: TrackPixelPayload) => Promise<PixelTrackingResult>;
  isLoading: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * React hook for tracking Facebook Pixel events
 *
 * Features:
 * - Debouncing to prevent duplicate events
 * - FBP/FBC cookie capture for Event Match Quality
 * - Error handling and logging
 * - Loading state management
 * - Optional enable/disable
 * - Callbacks for success/error
 * - Fire-and-forget pattern (non-blocking)
 *
 * Usage:
 * ```tsx
 * const { trackEvent, isLoading, error } = useFacebookPixel();
 *
 * const handleVenueSelect = async (venueId: number) => {
 *   await trackEvent({
 *     venueId,
 *     eventType: 'PageView',
 *     eventData: {
 *       content_name: 'Venue Page View',
 *     },
 *   });
 * };
 * ```
 */
export function useFacebookPixel(
  options: UseFacebookPixelOptions = {}
): UseFacebookPixelReturn {
  const {
    enabled = true,
    debounceMs = 300,
    onSuccess,
    onError,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trackedEventsRef = useRef<Set<string>>(new Set());
  const sessionIdRef = useRef<string>('');

  // Initialize session ID on first use (for guest tracking)
  if (!sessionIdRef.current) {
    sessionIdRef.current = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Capture Facebook's FBP (browser ID) and FBC (click ID) cookies
   * These cookies are set by the Facebook Pixel and help with Event Match Quality
   * FBP: User's browser ID (persistent) - Format: fb.1.timestamp.randomvalue
   * FBC: Click ID (from Facebook ads) - Format: fb.1.timestamp.fbclid
   *
   * Validates cookie format before inclusion to prevent tampering
   */
  const getCookieValues = useCallback((): { fbp?: string; fbc?: string } => {
    if (typeof document === 'undefined') return {};

    const cookies: { fbp?: string; fbc?: string } = {};

    try {
      const fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
      if (fbpMatch) {
        const fbpValue = fbpMatch[1];
        // Validate FBP format: fb.1.<timestamp>.<randomvalue>
        if (/^fb\.\d+\.\d+\.\d+$/.test(fbpValue)) {
          cookies.fbp = fbpValue;
        }
      }

      const fbcMatch = document.cookie.match(/_fbc=([^;]+)/);
      if (fbcMatch) {
        const fbcValue = fbcMatch[1];
        // Validate FBC format: fb.1.<timestamp>.<fbclid>
        if (/^fb\.\d+\.\d+\.[a-zA-Z0-9_-]+$/.test(fbcValue)) {
          cookies.fbc = fbcValue;
        }
      }
    } catch (err) {
      // Silent fail - cookie reading issues shouldn't block tracking
      if (process.env.NODE_ENV === 'development') {
        console.debug('[FacebookPixel] Failed to read Facebook cookies:', err);
      }
    }

    return cookies;
  }, []);

  // Generate event key for deduplication
  const getEventKey = useCallback((payload: TrackPixelPayload): string => {
    return `${payload.venueId}_${payload.eventType}_${JSON.stringify(payload.eventData || {})}`;
  }, []);

  // Track event with debouncing and deduplication
  const trackEvent = useCallback(
    async (payload: TrackPixelPayload): Promise<PixelTrackingResult> => {
      if (!enabled) {
        return {
          success: false,
          error: 'Facebook Pixel tracking is disabled',
          timestamp: Date.now(),
        };
      }

      const eventKey = getEventKey(payload);

      // Check if event was already tracked
      if (trackedEventsRef.current.has(eventKey)) {
        console.debug('[FacebookPixel] Event already tracked, skipping duplicate', eventKey);
        return {
          success: false,
          error: 'Event already tracked',
          timestamp: Date.now(),
        };
      }

      return new Promise((resolve) => {
        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(async () => {
          try {
            setIsLoading(true);
            setError(null);

            // Capture FBP and FBC cookies for Event Match Quality
            const cookies = getCookieValues();

            // Add cookies and session ID to custom fields
            const enhancedPayload: TrackPixelPayload = {
              ...payload,
              eventData: {
                ...payload.eventData,
                custom_fields: {
                  ...payload.eventData?.custom_fields,
                  ...(cookies.fbp && { fbp: cookies.fbp }),
                  ...(cookies.fbc && { fbc: cookies.fbc }),
                  session_id: sessionIdRef.current, // Always include session ID for guest tracking
                },
              },
            };

            // Fire-and-forget: Send tracking request without blocking UI
            // The server action handles its own error handling
            console.log('[FacebookPixel] Calling server action with payload:', {
              venueId: payload.venueId,
              eventType: payload.eventType,
            });
            const trackingPromise = trackPixelEvent(enhancedPayload);

            // Wait for the tracking request to complete for error handling
            // but don't show loading state that would impact UX
            console.log('[FacebookPixel] Waiting for server response...');
            const result = await trackingPromise;
            console.log('[FacebookPixel] Server response received:', {
              success: result.success,
              errorCode: result.errorCode,
            });

            if (result.success) {
              // Mark event as tracked
              trackedEventsRef.current.add(eventKey);

              // Call success callback
              onSuccess?.(result);

              // Always log success for debugging (all environments)
              console.log('[FacebookPixel] Event tracked successfully:', {
                eventType: payload.eventType,
                venueId: payload.venueId,
                eventId: result.eventId,
                cookies: Object.keys(cookies),
              });
            } else {
              const err = new Error(result.error || 'Failed to track event');
              setError(err);
              onError?.(err);

              // Always log errors for debugging (all environments)
              console.error('[FacebookPixel] Event tracking failed:', {
                eventType: payload.eventType,
                venueId: payload.venueId,
                error: result.error,
                errorCode: result.errorCode,
              });
            }

            resolve(result);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            onError?.(error);

            // Always log catch errors for debugging (all environments)
            console.error('[FacebookPixel] Exception during event tracking:', error);

            resolve({
              success: false,
              error: error.message,
              errorCode: 'EXCEPTION',
              timestamp: Date.now(),
            });
          } finally {
            setIsLoading(false);
          }
        }, debounceMs);
      });
    },
    [enabled, debounceMs, getEventKey, getCookieValues, onSuccess, onError]
  );

  // Reset hook state and tracked events
  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
    trackedEventsRef.current.clear();

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    trackEvent,
    isLoading,
    error,
    reset,
  };
}

/**
 * High-level hook for tracking venue page view events
 * Convenient wrapper for common venue tracking use case
 */
export function useVenuePageView() {
  const { trackEvent, isLoading, error } = useFacebookPixel();

  const trackVenuePageView = useCallback(
    async (venueId: number, venueName?: string) => {
      return trackEvent({
        venueId,
        eventType: 'PageView',
        eventData: {
          content_name: venueName || 'Venue Page View',
          content_type: 'venue',
        },
      });
    },
    [trackEvent]
  );

  return {
    trackVenuePageView,
    isLoading,
    error,
  };
}

/**
 * High-level hook for tracking booking-related events
 */
export function useBookingPixelTracking() {
  const { trackEvent, isLoading, error } = useFacebookPixel();

  const trackAddToCart = useCallback(
    async (venueId: number, showId: string, price: number, currency: string = 'DKK') => {
      return trackEvent({
        venueId,
        eventType: 'AddToCart',
        eventData: {
          value: price,
          currency,
          content_name: 'Show Booking',
          content_type: 'booking',
          content_ids: [showId],
        },
      });
    },
    [trackEvent]
  );

  const trackInitiateCheckout = useCallback(
    async (venueId: number, totalAmount: number, itemCount: number, currency: string = 'DKK') => {
      return trackEvent({
        venueId,
        eventType: 'InitiateCheckout',
        eventData: {
          value: totalAmount,
          currency,
          content_type: 'booking',
          custom_fields: {
            item_count: itemCount,
          },
        },
      });
    },
    [trackEvent]
  );

  const trackPurchase = useCallback(
    async (
      venueId: number,
      bookingId: string,
      totalAmount: number,
      currency: string = 'DKK'
    ) => {
      return trackEvent({
        venueId,
        eventType: 'Purchase',
        eventData: {
          value: totalAmount,
          currency,
          content_name: 'Booking Confirmation',
          content_type: 'booking',
          content_ids: [bookingId],
        },
      });
    },
    [trackEvent]
  );

  const trackLead = useCallback(
    async (venueId: number, formType: string = 'booking_inquiry') => {
      return trackEvent({
        venueId,
        eventType: 'Lead',
        eventData: {
          content_name: 'Booking Inquiry',
          content_type: 'form',
          custom_fields: {
            form_type: formType,
          },
        },
      });
    },
    [trackEvent]
  );

  return {
    trackAddToCart,
    trackInitiateCheckout,
    trackPurchase,
    trackLead,
    isLoading,
    error,
  };
}
